// Copy of IntakeTabBar but with the addition of deletion protection of stakeholders with jobs attached to them
import { useState } from "react";
import { ExternalLinkIcon, PlusIcon } from "lucide-react";

import { ConfirmationDialog, Column, Row, Text } from "@/components";
import { IntakeTab } from "@/components/companies/shared/IntakeTab";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useCreateAddStakeholder } from "@/hooks/createAdds/useCreateAddStakeholder";
import { useDeleteRemoveStakeholder } from "@/hooks/deleteRemoves/useDeleteRemoveStakeholder";
import { intake } from "@/lib/intakeProgress";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { StakeholderWithJobInfo } from "@/types";

export const StakeholdersIntakeTabBar = ({
  stakeholders,
  selectedId,
  setIsEditing,
  setSelectedId,
}: {
  stakeholders: StakeholderWithJobInfo[];
  selectedId: number | undefined;
  setIsEditing: (isEditing: boolean) => void;
  setSelectedId: (id: number | undefined) => void;
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const { mutate: createStakeholder, isPending: isCreatePending } =
    useCreateAddStakeholder();
  const { mutate: deleteStakeholder } = useDeleteRemoveStakeholder();

  const successToast = useSuccessToast();

  const resolvedSelectedId = selectedId ?? stakeholders[0]?.id;
  const selectedStakeholder = stakeholders.find(
    (s) => s.id === resolvedSelectedId,
  );
  const hasJobs =
    selectedStakeholder && selectedStakeholder.jobStakeholders.length > 0;

  const handleAdd = () => {
    setIsEditing(true);
    createStakeholder(undefined, {
      onSuccess: (newStakeholder) => {
        setSelectedId(newStakeholder.id);
      },
    });
  };

  const handleDelete = () => {
    deleteStakeholder(
      { id: resolvedSelectedId },
      {
        onSuccess: () => {
          if (selectedId === resolvedSelectedId) {
            const next = stakeholders.find((s) => s.id !== resolvedSelectedId);
            setSelectedId(next?.id);
            successToast({ description: "Stakeholder deleted successfully." });
          }
        },
      },
    );
  };

  const removeButton = (
    <Button
      className="h-8 border-red-300 px-3 text-xs"
      disabled={!!hasJobs}
      onClick={() => {
        setIsDeleteDialogOpen(true);
      }}
      variant="outlineDestructive"
    >
      Remove
    </Button>
  );

  const render = () => (
    <Row className="items-center border-b border-slate-200">
      <div className="relative min-w-0 flex-1">
        <Row className="no-scrollbar items-center overflow-x-auto">
          {stakeholders.map((stakeholder, index) => (
            <IntakeTab
              key={stakeholder.id}
              progress={intake.getStakeholderProgress(stakeholder)}
              isSelected={resolvedSelectedId === stakeholder.id}
              label={
                `${stakeholder.firstName ?? ""} ${stakeholder.lastName ?? ""}`.trim() ||
                `Stakeholder ${String(index + 1)}`
              }
              onSelect={() => {
                setSelectedId(stakeholder.id);
              }}
            />
          ))}
        </Row>
        <div className="pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-white to-transparent" />
      </div>
      <Row className="ml-auto shrink-0 items-center gap-2 pl-4">
        <Button
          className="h-8 shrink-0 gap-1.5 px-3 text-xs"
          disabled={isCreatePending}
          onClick={() => {
            mixpanelUtils.trackButtonClick(
              "Add Stakeholder",
              MIXPANEL_SCREENS.COMPANY,
            );
            handleAdd();
          }}
          size="sm"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add Stakeholder
        </Button>
        {hasJobs ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>{removeButton}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs p-3">
                <Column className="gap-3">
                  <Column className="gap-1.5">
                    <Text className="text-xs font-medium">
                      This stakeholder can&apos;t be deleted because it is
                      assigned to the following jobs:
                    </Text>
                    <ul className="flex flex-col gap-1">
                      {selectedStakeholder.jobStakeholders.map(({ job }) => (
                        <li key={job.id}>
                          <a
                            className="inline-flex items-center gap-1 text-xs text-primary-600 underline underline-offset-2 hover:text-primary-700"
                            href={`/companies/${selectedStakeholder.companyId}/jobs/${job.id}?t=job-intake`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {job.title}
                            <ExternalLinkIcon className="h-3 w-3" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </Column>
                  <Text className="mt-2 text-xs" variant="muted">
                    Deleting this stakeholder is permanent. If you delete them
                    here, they will be removed from all jobs they are associated
                    with. If you want to still delete this stakeholder,{" "}
                    <Button
                      className="h-auto p-0 text-xs font-medium text-red-600 underline underline-offset-2 hover:text-red-700"
                      onClick={() => {
                        setIsDeleteDialogOpen(true);
                      }}
                      variant="link"
                    >
                      click here
                    </Button>
                    .
                  </Text>
                </Column>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          removeButton
        )}
      </Row>
      <ConfirmationDialog
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description="This action cannot be undone. This will permanently delete the stakeholder."
        onConfirm={() => {
          mixpanelUtils.trackButtonClick(
            "Remove Stakeholder",
            MIXPANEL_SCREENS.COMPANY,
          );
          setIsDeleteDialogOpen(false);
          handleDelete();
        }}
        onOpenChange={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
        title="Are you sure?"
      />
    </Row>
  );

  return render();
};
